import axios, { ResponseType as AxiosResponseType, Method as AxiosHttpMethod, AxiosError, AxiosResponse, AxiosRequestConfig } from "axios";
import { AgTechApiEntityResponse, AgTechHttpResponse, AgTechHttpResponses } from "../actions/AgTechHttpRequests";
import { AgTechApiError } from "./AgTechCoreApiRequests";
import { IAgTechAppContext, useAppContext } from "../../app/AgTechAppContext";
import { AgTechDataActionLoggingContext } from "../actions/AgTechDataActions";
import { logDevError, logEntry, logEntryError } from "../../logging/AgTechLogger";
import { isDev } from "../../utilities/AgTechUtilities";
import { useDispatch } from "react-redux";
import { useAgTechAuthContext } from "../../auth/AgTechAuthContext";
import { useAgTechDataContext } from "../common/AgTechDataContext";
import { Dispatch } from "@reduxjs/toolkit";
import { MutableRefObject } from "react";

///
/// Request props
///

export type AgTechCoreHttpRequestProps = {
    path: string,
    baseURL?: string
};

export type AgTechStandardHttpRequestProps = AgTechCoreHttpRequestProps | string;

export type AgTechRequestProps<TResponseData, TProjectedData = TResponseData> = {
    request: AgTechStandardHttpRequestProps,
    convert?: (result: TResponseData) => TProjectedData
}

export type AgTechWriteRequestProps<TPostedData, TResponseData, TProjectionData = TResponseData> = AgTechRequestProps<TResponseData, TProjectionData> & {
    inputData: TPostedData
};

///
/// Request Context
///

type AgTechHttpRequestContextData = {
    baseURL: string,
    onError?: (error: AgTechApiError) => Promise<void>,
    appContext: IAgTechAppContext
    token: string,
    logout: () => Promise<void>,
    dispatch: Dispatch<any>
}

export const useAgTechHttpRequestContext = (): AgTechHttpRequestContextData => {
    const appContext = useAppContext();
    const authContext = useAgTechAuthContext();
    const dataContext = useAgTechDataContext();
    const dispatch = useDispatch();
    
    return {
        baseURL: dataContext.baseURL,
        token: authContext.user.token,
        logout: authContext.logoutUser,
        appContext: appContext,
        dispatch: dispatch
    }
};


///
/// Request management
///

export type AgTechHttpRequester = {
    loggingContext: any,
    // GET requests
    get: <TResponse, TProjection = TResponse>(request: AgTechRequestProps<TResponse, TProjection>) => Promise<AgTechHttpResponse<TProjection>>,
    getEntity: <TResponse, TProjection = TResponse>(request: AgTechRequestProps<TResponse, TProjection>) => Promise<AgTechHttpResponse<TProjection>>,
    getImage: (request: AgTechStandardHttpRequestProps) => Promise<AgTechHttpResponse<string>>,
    getFile: (request: AgTechStandardHttpRequestProps) => Promise<AgTechHttpResponse<Blob>>,
    downloadFile: (request: AgTechStandardHttpRequestProps, destinationFileNameWithExtension: string) => Promise<AgTechHttpResponse<string>>,

    // POST requests
    post: <TPostedData, TResponseData, TProjectionData = TResponseData>(
        request: AgTechWriteRequestProps<TPostedData, TResponseData, TProjectionData>,
    ) => Promise<AgTechHttpResponse<TProjectionData>>,
    submit: <TPostedData, TResposeData = TPostedData, TProjectionData = TResposeData>(
        props: AgTechWriteRequestProps<TPostedData, TResposeData, TProjectionData>
    ) => Promise<AgTechHttpResponse<TProjectionData>>,

    // PUT requests
    put: <TPostedData, TResponseData, TProjectionData = TResponseData>(
        props: AgTechWriteRequestProps<TPostedData, TResponseData, TProjectionData>
    ) => Promise<AgTechHttpResponse<TProjectionData>>
}

export const useAgTechHttpRequester = (loggingContext?: MutableRefObject<any>): AgTechHttpRequester => {
    const requestContext = useAgTechHttpRequestContext();

    return getAgTechHttpRequester(requestContext, loggingContext);
}

const getAgTechHttpRequester = (
    requestContext: AgTechHttpRequestContextData,
    loggingContext?: MutableRefObject<any>
): AgTechHttpRequester => {
    return {
        loggingContext: loggingContext,
        get: async <TResponse, TProjection = TResponse>(props: AgTechRequestProps<TResponse, TProjection>) => {
            return executeAgTechHttpRequest({
                request: {
                    type: 'GET',
                    props: props,
                    context: requestContext
                },
                loggingContext
            });
        },
        getEntity: async <TResponse, TProjection>(props: AgTechRequestProps<TResponse, TProjection>) => {
            return executeAgTechHttpRequest<any, AgTechApiEntityResponse<TResponse>, TProjection>({
                request: {
                    type: 'GET',
                    props: {
                        request: props.request,
                        convert: (entity: AgTechApiEntityResponse<TResponse>) => {
                            const entityData = entity.data;
                            
                            return props.convert ? props.convert(entityData) : entityData as unknown as TProjection;
                        }
                    },
                    context: requestContext,
                },
                loggingContext
            });
        },
        getImage: (props: AgTechStandardHttpRequestProps) => {
            return executeAgTechHttpRequest<any, any>({
                request: {
                    type: 'GET',
                    props: { 
                        request: props,
                        convert: data => {
                            return btoa(
                                new Uint8Array(data).reduce(
                                    (data, byte) => data + String.fromCharCode(byte),
                                    ''
                                )
                            );
                        }
                    },
                    context: requestContext
                },
                response: {
                    type: 'arraybuffer'
                },
                loggingContext
            });
        },
        getFile: (props: AgTechStandardHttpRequestProps) => {
            return executeAgTechHttpRequest<any, any>({
                request: {
                    type: 'GET',
                    props: { request: props },
                    context: requestContext
                },
                response: {
                    type: 'blob'
                },
                loggingContext
            });
        },
        downloadFile: async (props: AgTechStandardHttpRequestProps, destinationFileNameWithExtension: string) => {
            const fileResponse = await executeAgTechHttpRequest<any, any>({
                request: {
                    type: 'GET',
                    props: { request: props },
                    context: requestContext
                },
                response: {
                    type: 'blob'
                },
                loggingContext
            });

            if (fileResponse.success) {
                let documentBlob = new Blob([fileResponse.success?.data]);

                const mockDocumentURL = window.URL.createObjectURL(documentBlob);
                const documentDownloadLink = document.createElement('a');
                documentDownloadLink.href = mockDocumentURL;
                documentDownloadLink.setAttribute('download', destinationFileNameWithExtension);
                document.body.appendChild(documentDownloadLink);
                documentDownloadLink.click();
    
                return AgTechHttpResponses.Success(documentBlob);
            }
            else {
                return AgTechHttpResponses.Failed('Download failed.');
            }
        },
        post: async <TPostedData, TResponseData, TProjectionData = TResponseData>(
            props: AgTechWriteRequestProps<TPostedData, TResponseData, TProjectionData>
        ) => {
            return executeAgTechHttpRequest<TPostedData, TResponseData, TProjectionData>({
                request: {
                    type: 'POST',
                    props: props,
                    context: requestContext,
                    input: props.inputData
                },
                loggingContext
            });
        },
        submit: async <TPostedData, TResponseData = TPostedData, TProjectedData = TResponseData>(
            props: AgTechWriteRequestProps<TPostedData, TResponseData, TProjectedData>
        ) => {
            const requestProps = typeof props.request === 'string'
                ? { path: props.request as string }
                : props.request as AgTechCoreHttpRequestProps;

            return executeAgTechHttpRequest<TPostedData, AgTechApiEntityResponse<TResponseData>, TProjectedData>({
                request: {
                    type: 'POST',
                    props: {
                        request: {
                            ...requestProps,
                            path: requestProps.path + '/Submit',
                        },
                        convert: entity => props.convert ? props.convert(entity.data) : entity.data as unknown as TProjectedData
                    },
                    context: requestContext,
                    input: props.inputData
                },
                loggingContext
            });
        },
        put: async <TPostedData, TResponseData, TProjectionData = TResponseData>(
            props: AgTechWriteRequestProps<TPostedData, TResponseData, TProjectionData>
        ) => {
            return executeAgTechHttpRequest<TPostedData, TResponseData, TProjectionData>({
                request: {
                    type: 'PUT',
                    props: props,
                    context: requestContext,
                    input: props.inputData
                },
                loggingContext
            });
        }
    }
}

///
/// Request Execution
///

type AgTechHttpRequestExecutionProps<TInputData, TResponseData, TProjectionData = TResponseData> = {
    request: {
        type: AxiosHttpMethod,
        props: AgTechRequestProps<TResponseData, TProjectionData>,
        context: AgTechHttpRequestContextData,
        input?: TInputData
    },
    response?: {
        type?: AxiosResponseType
    },
    loggingContext?: MutableRefObject<any>
}

const executeAgTechHttpRequest = async <TInputData, TResponseData, TProjectionData = TResponseData>(
    props: AgTechHttpRequestExecutionProps<TInputData, TResponseData, TProjectionData>
): Promise<AgTechHttpResponse<TProjectionData>> =>
{
    const requestProps = typeof props.request.props.request === 'string'
        ? { path: props.request.props.request as string }
        : props.request.props.request as AgTechCoreHttpRequestProps;

    const requestBaseURL = requestProps.baseURL ?? props.request.context.baseURL;
    const requestURL = `${requestBaseURL}/api/${requestProps.path}`;

    const axiosRequest = {
        type: props.request.type,
        url: requestURL,
        inputData: props.request.input,
        context: props.request.context,
        axiosConfig: {
            responseType: props.response?.type,
            headers: { 
                Authorization: `Bearer ${props.request.context.token}`,
                // 'Content-Type': 'application/json',
            }
        }
    };

    const axiosRequestExecutor = getExecutorForAxiosRequest<TInputData, TResponseData>(axiosRequest);

    try
    {
        if (props.loggingContext) {
            props.loggingContext.current.http = {
                request: {
                    url: axiosRequest.url,
                    method: axiosRequest.type,
                    body: props.request.input
                }
            }
        }

        const axiosResponse = await axiosRequestExecutor.execute();

        let responseData = axiosResponse.data;

        if (axiosResponse.data && props.request.props.convert) {
            try
            {
                responseData = props.request.props.convert(axiosResponse.data);
            }
            catch (error) {
                logDevError('An error occurred mapping DTO data to an entity', error);

                return AgTechHttpResponses.Failed(error);
            }
        }

        if (props.loggingContext) {
            props.loggingContext.current.http = {
                ...props.loggingContext.current.http,
                response: {
                    status: axiosResponse.status,
                    data: axiosResponse.data
                },
                result: responseData
            }
        }
        
        return AgTechHttpResponses.Success(responseData);
    }
    catch (error) {
        handleFailedAxiosRequest(axiosRequest, error, props.loggingContext);

        return AgTechHttpResponses.Failed(error);
    }
}

///
/// Response handling
///

const handleFailedAxiosRequest = (
    request: AgTechAxiosRequest<any>,
    error: any,
    loggingContext?: MutableRefObject<any>
) => {
    logEntryError(`HTTP ${request.type} Request`, request.url, { error: error });

    if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;

        if (axiosError.response?.status === 401) {
            request.context.logout();
        }
        else if (isDev()) {
            const responseData = axiosError.response?.data;
            let responseErrorMessage = responseData?.errorMessages ? responseData.errorMessages[0]?.message : '';

            if (responseErrorMessage) {
                responseErrorMessage = responseErrorMessage.substring(0, 200);
            }

            const responseStatus = axiosError.response?.status ?? 404;
            const responseMessage = responseErrorMessage ? responseErrorMessage : axiosError.message;

            if (loggingContext) {
                loggingContext.current.http = {
                    ...loggingContext.current.http,
                    response: {
                        ...loggingContext.current.http.response,
                        status: responseStatus,
                        message: responseMessage
                    }
                };
            }

            (error as any).context = loggingContext?.current.http;

            throw error;
        }
    }
}

///
/// Axios
///

type AgTechAxiosRequest<TInputData> = {
    type: AxiosHttpMethod,
    url: string,
    context: AgTechHttpRequestContextData,
    axiosConfig: AxiosRequestConfig,
    inputData?: TInputData
};

type AgTechAxiosRequestExecutor<TResponseData> = {
    execute: () => Promise<AxiosResponse<any, TResponseData>>
}

const getExecutorForAxiosRequest = <TInputData, TResponseData>(
    request: AgTechAxiosRequest<TInputData>
): AgTechAxiosRequestExecutor<TResponseData> => {
    const { type, url, axiosConfig, inputData } = request;

    const executionFunc = type === 'POST' ? () => axios.post<TResponseData>(url, inputData, axiosConfig)
      : type === 'PUT' ? () => axios.put<TResponseData>(url, inputData, axiosConfig)
      : () => axios.get<TResponseData>(url, axiosConfig);

    return {
        execute: executionFunc
    }
}